import { Suspense, useEffect, useState, lazy } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { getPost, getAdjacentPosts } from './postRegistry';
import type { PostEntry, PostMeta } from './types';
import { BlogLayout } from './components/BlogLayout';
import { BlogMDXProvider } from './components/BlogMDXProvider';
import { BlogPostContext } from './context/BlogPostContext';
import type { ComponentType } from 'react';

function PostSkeleton() {
  return (
    <div className="min-h-full bg-background px-4 py-12">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="h-9 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className="mt-10 space-y-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-4 animate-pulse rounded bg-muted"
              style={{ width: `${70 + Math.random() * 25}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

type LoadState =
  | { status: 'loading' }
  | { status: 'notFound' }
  | {
      status: 'ready';
      entry: PostEntry;
      Component: ComponentType;
      prev: PostMeta | null;
      next: PostMeta | null;
    };

export function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    if (!slug) {
      setState({ status: 'notFound' });
      return;
    }

    setState({ status: 'loading' });

    Promise.all([getPost(slug), getAdjacentPosts(slug)]).then(
      ([entry, { prev, next }]) => {
        if (!entry) {
          setState({ status: 'notFound' });
          return;
        }

        const Component = lazy(
          () => entry.load() as Promise<{ default: ComponentType }>,
        );
        setState({ status: 'ready', entry, Component, prev, next });
      },
    );
  }, [slug]);

  if (state.status === 'loading') {
    return <PostSkeleton />;
  }

  if (state.status === 'notFound') {
    return <Navigate to="/blog" replace />;
  }

  const { entry, Component, prev, next } = state;

  return (
    <BlogPostContext.Provider
      value={{ rawContent: entry.rawContent, meta: entry.meta }}
    >
      <BlogMDXProvider>
        <BlogLayout meta={entry.meta} prev={prev} next={next}>
          <Suspense fallback={<PostSkeleton />}>
            <Component />
          </Suspense>
        </BlogLayout>
      </BlogMDXProvider>
    </BlogPostContext.Provider>
  );
}
